import { isOperationOutcomeBlocking, restoreOperationOutcome, runOperationOutcome } from "../../../packages/ui/src/safety/operationOutcome.js";

const REGISTRATION_STATES = new Set(["open", "not_open", "closed", "full"]);
const REGISTRATION_LIFECYCLE_STATUSES = new Set(["published", "registration_open", "registration_closed"]);
export const COMPETITION_REGISTRATION_OUTCOME_NAMESPACE = "competition-register";

export async function runCompetitionRegistrationOutcome({
  competitionId,
  destination,
  execute,
  expectedCompetitionId,
  outcomeOptions = {},
  request,
}) {
  if (typeof request !== "function") throw registrationOutcomeError("COMPETITION_REGISTRATION_REQUEST_REQUIRED");
  if (typeof execute !== "function") throw registrationOutcomeError("COMPETITION_REGISTRATION_EXECUTE_REQUIRED");
  const { dtoCompetitionId, scope } = await resolveCompetitionRegistrationScope({ competitionId, destination, expectedCompetitionId, request });
  const runtime = registrationOutcomeRuntime(outcomeOptions);
  return runOperationOutcome({
    namespace: COMPETITION_REGISTRATION_OUTCOME_NAMESPACE,
    scope,
    storage: runtime.storage,
    lockManager: runtime.lockManager,
    execute,
    validateResponse: (data) => validateRegistrationMutationResponse(data, dtoCompetitionId),
    uuid: runtime.uuid,
    now: runtime.now,
  });
}

export async function restoreCompetitionRegistrationOutcome({
  competitionId,
  destination,
  expectedCompetitionId,
  outcomeOptions = {},
  request,
  signal,
}) {
  if (typeof request !== "function") throw registrationOutcomeError("COMPETITION_REGISTRATION_REQUEST_REQUIRED");
  const { scope } = await resolveCompetitionRegistrationScope({ competitionId, destination, expectedCompetitionId, request, signal });
  const runtime = registrationOutcomeRuntime(outcomeOptions);
  return isOperationOutcomeBlocking(restoreOperationOutcome({
    namespace: COMPETITION_REGISTRATION_OUTCOME_NAMESPACE,
    scope,
    storage: runtime.storage,
  }));
}

export function isUnknownCompetitionRegistrationOutcome(error) {
  const code = String(error?.code || error?.details?.code || "").trim();
  return code === "OPERATION_OUTCOME_RESULT_UNKNOWN" || code === "OPERATION_OUTCOME_PENDING";
}

export function scheduleCancellableMicrotask(task, schedule = queueMicrotask) {
  let active = true;
  schedule(() => {
    if (active) task();
  });
  return () => {
    active = false;
  };
}

export function runRegistrationSingleFlight(holder, factory) {
  if (holder.current) return holder.current;
  const operation = Promise.resolve().then(factory);
  holder.current = operation;
  const clear = () => {
    if (holder.current === operation) holder.current = null;
  };
  operation.then(clear, clear);
  return operation;
}

export function getRegistrationAvailability(competition = {}) {
  const source = competition?.registrationAvailability;
  const state = REGISTRATION_STATES.has(source?.state) ? source.state : "unavailable";
  const capacitySource = source?.capacity || {};
  const max = Number.isFinite(Number(capacitySource.max)) && capacitySource.max !== null
    ? Number(capacitySource.max)
    : null;
  const used = Number.isFinite(Number(capacitySource.used))
    ? Math.max(0, Number(capacitySource.used))
    : 0;

  return {
    state,
    canRegister: state === "open" && source?.canRegister === true,
    reason: source?.reason || (state === "unavailable" ? "Disponibilidad de inscripción no confirmada." : null),
    capacity: {
      used,
      max,
      isFull: source?.capacity?.isFull === true,
    },
  };
}

export function isRegistrationAvailable(competition = {}) {
  return getRegistrationAvailability(competition).canRegister;
}

export function registrationEntryLabel(registrationState = null) {
  const step = String(registrationState?.status?.step || "").toLowerCase();
  const entryStatus = String(registrationState?.existingEntry?.status || "").toLowerCase();
  if (step !== "registered" && !["approved", "pending", "waitlist"].includes(entryStatus)) return "";
  if (entryStatus === "pending") return "Inscripción pendiente";
  if (entryStatus === "waitlist") return "Lista de espera";
  return "Inscrito";
}

export function registrationFormForState(data = {}, current = {}, selections = {}) {
  const availableCars = Array.isArray(data.availableCars) ? data.availableCars : [];
  const carClassCategories = (data.availableCategories || []).filter((category) => category.categoryType === "car_class");
  const gameAccounts = (data.eligibility?.requiredAccounts || [])
    .flatMap((requirement) => requirement.accounts || [])
    .filter((account) => account.verificationStatus === "verified" && account.status === "active" && !account.identityConflict);
  const availableSkins = Array.isArray(data.availableSkins) ? data.availableSkins : [];
  const selectedGameAccountId = String(selections.selectedGameAccountId || "");
  const selectedCarId = String(selections.selectedCarId || "");
  const detectedAccountId = String(data.driverClassDetection?.selectedAccountId || "");
  const currentCarId = availableCars.some((item) => String(item.id) === String(current.carId || "")) ? String(current.carId) : "";
  const currentCarClassId = carClassCategories.some((item) => String(item.carClassId || item.id) === String(current.carClassId || ""))
    ? String(current.carClassId)
    : "";
  const currentGameAccountId = gameAccounts.some((item) => String(item.id) === String(current.gameAccountId || ""))
    ? String(current.gameAccountId)
    : "";
  const currentSkinId = availableSkins.some((item) => String(item.id) === String(current.skinId || "")) ? String(current.skinId) : "";
  const firstSkin = availableSkins.length === 1 ? availableSkins[0] : null;
  const nextSkinId = currentSkinId || (firstSkin ? String(firstSkin.id) : "");
  const selectedSkin = availableSkins.find((item) => String(item.id) === nextSkinId) || null;

  return {
    ...current,
    carId: selectedCarId || currentCarId || (availableCars.length === 1 ? String(availableCars[0].id) : ""),
    carClassId: currentCarClassId || (carClassCategories.length === 1
      ? String(carClassCategories[0].carClassId || carClassCategories[0].id)
      : ""),
    gameAccountId: selectedGameAccountId || detectedAccountId || currentGameAccountId || (gameAccounts.length === 1 ? String(gameAccounts[0].id) : ""),
    skinId: nextSkinId,
    raceNumber: data.registrationRequirements?.raceNumber?.mode === "derived_from_skin" && selectedSkin
      ? String(selectedSkin.raceNumber || "")
      : String(current.raceNumber || ""),
  };
}

export function getRegistrationPresentation(competition = {}) {
  const availability = getRegistrationAvailability(competition);
  const lifecycleStatus = String(competition?.statusKey || competition?.status || "").toLowerCase();

  if (competition?.registrationMode === "invite_only") {
    return {
      applies: true,
      managedParticipation: true,
      statusKey: "registration_closed",
      statusLabel: "Participación por invitación",
      ctaLabel: competition?.competitionType === "championship" ? "Ver campeonato" : "Ver evento",
      ctaTone: "outline",
      availability,
    };
  }

  if (availability.canRegister) {
    return {
      applies: true,
      managedParticipation: false,
      statusKey: "registration_open",
      statusLabel: "Inscripción abierta",
      ctaLabel: "Inscribirme",
      ctaTone: "primary",
      availability,
    };
  }

  if (availability.state === "not_open") {
    return {
      applies: true,
      managedParticipation: false,
      statusKey: "registration_not_open",
      statusLabel: "Inscripciones próximas",
      ctaLabel: "Ver evento",
      ctaTone: "outline",
      availability,
    };
  }

  if (availability.state === "full") {
    return {
      applies: true,
      managedParticipation: false,
      statusKey: "registration_full",
      statusLabel: "Cupos completos",
      ctaLabel: "Ver evento",
      ctaTone: "outline",
      availability,
    };
  }

  if (availability.state === "closed" && REGISTRATION_LIFECYCLE_STATUSES.has(lifecycleStatus)) {
    return {
      applies: true,
      managedParticipation: false,
      statusKey: "registration_closed",
      statusLabel: "Inscripción cerrada",
      ctaLabel: "Ver evento",
      ctaTone: "outline",
      availability,
    };
  }

  if (availability.state === "unavailable" && lifecycleStatus === "registration_open") {
    return {
      applies: true,
      managedParticipation: false,
      statusKey: "registration_unavailable",
      statusLabel: "Disponibilidad no confirmada",
      ctaLabel: "Ver evento",
      ctaTone: "outline",
      availability,
    };
  }

  return {
    applies: false,
    managedParticipation: false,
    statusKey: lifecycleStatus,
    statusLabel: "",
    ctaLabel: "Ver evento",
    ctaTone: "outline",
    availability,
  };
}

function canonicalRegistrationDestination(value) {
  let url;
  try {
    url = new URL(String(value || ""));
  } catch {
    throw registrationOutcomeError("COMPETITION_REGISTRATION_DESTINATION_INVALID");
  }
  if (!["http:", "https:"].includes(url.protocol) || url.username || url.password || url.search || url.hash) {
    throw registrationOutcomeError("COMPETITION_REGISTRATION_DESTINATION_INVALID");
  }
  const path = url.pathname.replace(/\/+$/, "");
  return `${url.origin}${path && path !== "/" ? path : ""}`;
}

async function resolveCompetitionRegistrationScope({ competitionId, destination, expectedCompetitionId, request, signal }) {
  const dtoCompetitionId = positiveRegistrationId(competitionId, "COMPETITION_REGISTRATION_ID_INVALID");
  if (expectedCompetitionId !== undefined && expectedCompetitionId !== null && expectedCompetitionId !== "") {
    const visibleCompetitionId = positiveRegistrationId(expectedCompetitionId, "COMPETITION_REGISTRATION_EXPECTED_ID_INVALID");
    if (visibleCompetitionId !== dtoCompetitionId) throw registrationOutcomeError("COMPETITION_REGISTRATION_ID_MISMATCH");
  }
  const auth = await request("/api/v1/auth/me", { cache: "no-store", ...(signal ? { signal } : {}) });
  const actorId = positiveRegistrationId(auth?.user?.id, "COMPETITION_REGISTRATION_ACTOR_INVALID");
  return Object.freeze({
    dtoCompetitionId,
    scope: Object.freeze({
      actorId,
      destination: canonicalRegistrationDestination(destination),
      competitionId: dtoCompetitionId,
      operation: "register",
    }),
  });
}

function registrationOutcomeRuntime(options) {
  const cryptoRef = options.cryptoRef ?? globalThis.crypto;
  return Object.freeze({
    lockManager: options.lockManager ?? globalThis.navigator?.locks,
    now: options.now || (() => new Date().toISOString()),
    storage: options.storage ?? globalThis.localStorage,
    uuid: options.uuid || (() => cryptoRef?.randomUUID?.()),
  });
}

function validateRegistrationMutationResponse(data, competitionId) {
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    throw registrationOutcomeError("COMPETITION_REGISTRATION_RESPONSE_INVALID");
  }
  const responseCompetitionId = positiveRegistrationId(data.competition?.id, "COMPETITION_REGISTRATION_RESPONSE_INVALID");
  if (responseCompetitionId !== competitionId || data.status?.step !== "registered") {
    throw registrationOutcomeError("COMPETITION_REGISTRATION_RESPONSE_INVALID");
  }
}

function positiveRegistrationId(value, code) {
  const number = Number(value);
  if (!Number.isSafeInteger(number) || number <= 0) throw registrationOutcomeError(code);
  return number;
}

function registrationOutcomeError(code) {
  const error = new Error(code);
  error.code = code;
  return error;
}
